import { get, getBlob, toQueryParams } from '@/lib/http';
import { endpoints } from './endpoints';
import type {
  ExportFormat,
  Page,
  ReconciliationRow,
  ReportFilters,
  ReportRow,
  ReportTotals,
  ReportType,
  ShipmentRow,
} from '@/types';

/** Report types whose rows are one per lot (from commercial info), not one per carton. */
export type LotReportType = 'RECONCILIATION' | 'SHIPMENT';

export const reportApi = {
  /** Every carton-based type. RECONCILIATION/SHIPMENT return other row shapes — use their own calls. */
  rows: (type: Exclude<ReportType, LotReportType>, filters: ReportFilters = {}) =>
    get<Page<ReportRow>>(endpoints.reports.rows(type), {
      params: toQueryParams({ page: 0, size: 10, ...filters }),
    }),

  /** One row per fully-dispatched lot with its commercial info entered. */
  reconciliationRows: (filters: ReportFilters = {}) =>
    get<Page<ReconciliationRow>>(endpoints.reports.rows('RECONCILIATION'), {
      params: toQueryParams({ page: 0, size: 10, ...filters }),
    }),

  /** The shipment register — one row per shipped lot with its commercial info entered. */
  shipmentRows: (filters: ReportFilters = {}) =>
    get<Page<ShipmentRow>>(endpoints.reports.rows('SHIPMENT'), {
      params: toQueryParams({ page: 0, size: 10, ...filters }),
    }),

  totals: (type: ReportType, filters: ReportFilters = {}) =>
    get<ReportTotals>(endpoints.reports.totals(type), {
      params: toQueryParams({ ...filters, page: undefined, size: undefined }),
    }),

  /**
   * Server-side export. Spring Boot should stream this back with
   * `Content-Disposition: attachment; filename=...`.
   */
  export: (type: ReportType, format: ExportFormat, filters: ReportFilters = {}) =>
    getBlob(endpoints.reports.export(type), {
      params: toQueryParams({ ...filters, format, page: undefined, size: undefined }),
    }),
};
